import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Search, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductsPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const productsRes = await axios.get(`${API}/products`);
        
        // Filtrer uniquement les produits actifs
        const activeProducts = productsRes.data.products.filter(p => p.isActive);
        setProducts(activeProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Extraire les catégories uniques
  const uniqueCategories = [...new Set(products.map(p => p.category).filter(Boolean))];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDF8F3] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#C9A96E] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* Hero */}
      <section className="relative py-24 bg-[#2C1810]">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-[#C9A96E] font-medium uppercase tracking-wider text-sm">Nos Produits</span>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mt-4 mb-6">
            Boutique Beauté
          </h1>
          <div className="w-20 h-1 bg-gradient-to-r from-[#C9A96E] to-[#A67B5B] mx-auto mb-6" />
          <p className="text-[#D4C4B5] text-lg max-w-2xl mx-auto">
            Découvrez notre sélection de produits de beauté afro de qualité professionnelle.
          </p>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-12">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A67B5B]" size={20} />
                <Input
                  type="text"
                  placeholder="Rechercher un produit..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-[#D4C4B5] focus:border-[#C9A96E]"
                />
              </div>
            </div>
            <div className="w-full md:w-64">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="border-[#D4C4B5] focus:border-[#C9A96E]">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les catégories</SelectItem>
                  {uniqueCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Products Grid */}
          {filteredProducts.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag size={48} className="mx-auto text-[#D4C4B5] mb-4" />
              <p className="text-[#5D4037]">Aucun produit disponible pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {filteredProducts.map((product) => (
                <div 
                  key={product.id} 
                  onClick={() => navigate(`/produits/${product.id}`)}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer"
                >
                  <div className="aspect-square bg-[#F5F0EB] relative">
                    {product.image ? (
                      <img 
                        src={product.image.startsWith('/uploads') ? `${BACKEND_URL}${product.image}` : product.image} 
                        alt={product.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag size={48} className="text-[#D4C4B5]" />
                      </div>
                    )}
                    {product.stock <= 5 && product.stock > 0 && (
                      <div className="absolute top-3 right-3 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                        Stock faible
                      </div>
                    )}
                    {product.stock === 0 && (
                      <div className="absolute top-3 right-3 bg-gray-700 text-white text-xs px-2 py-1 rounded-full">
                        Épuisé
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <p className="text-xs text-[#A67B5B] uppercase mb-1">{product.brand}</p>
                    <h3 className="text-lg font-semibold text-[#2C1810] mb-2 line-clamp-2">{product.name}</h3>
                    <p className="text-sm text-[#5D4037] mb-4 line-clamp-2">{product.description}</p>
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold text-[#C9A96E]">{product.price?.toFixed(2)}€</span>
                      <Button 
                        className="bg-[#C9A96E] hover:bg-[#A67B5B] text-white"
                        disabled={product.stock === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/produits/${product.id}`);
                        }}
                      >
                        Voir détails
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
